/* assets/ahm-car.js — the vehicle page (slot 'car', sections/main-car.liquid) and
   the sold page (slot 'sold', sections/main-404.liquid on /products/* 404s).
   The car record comes from window.AHM.car (car-json, full) and related / similar
   cars from the stock feed. Liquid server-renders the essentials (h1, availability,
   key specs, description, gallery, JSON-LD); this script re-renders the full page.
   Section copy comes from data-* attributes on the slot (theme editor settings).
   Nothing shown is invented: every line is computed from the car record or is a
   real business fact kept in the section settings.
   Local helpers are prefixed cv (car) / sd (sold). */
(function () {
  'use strict';
  var M = window.M;
  if (!M) return;
  var esc = M.esc;
  var A = window.AHM || {};

  /* ------------------------------------------------------------------ state */
  var ST = {};
  function st(ref) { return ST[ref] || (ST[ref] = { idx: 0, qs: [M.QUESTIONS[0]], name: '', note: '' }); }
  var io = null;          // IntersectionObserver for the phone bar
  var curRef = null;
  var renders = 0;        // 1st render = inline car only; later renders = the feed has arrived
  var descHTML = '';      // server-rendered description (merchant's own copy), captured at init

  /* ------------------------------------------------------------------ utils */
  function cap(s) { s = String(s || '').trim(); return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }
  function lc(s) { return String(s || '').toLowerCase(); }
  function smooth() { return M.reducedMotion ? 'auto' : 'smooth'; }
  function needed(note, cls) { return '<div class="content-needed' + (cls ? ' ' + cls : '') + '"><span class="content-needed__label">Content needed</span>' + note + '</div>'; }
  function isPartner() { return !!(M.review && M.review.partnerNote); }
  function cfg(el, k, d) { var v = el && el.getAttribute('data-' + k); return v != null && String(v).trim() !== '' ? v : d; }
  function uniqCI(list) {
    var seen = {}; return (list || []).map(cap).filter(function (f) { var k = lc(f); if (!k || seen[k]) return false; seen[k] = 1; return true; });
  }
  function phone() { return String(A.phone || '').trim(); }
  function telHref(p) { return 'tel:' + String(p).replace(/[^\d+]/g, ''); }
  function findUrl(p) {
    var q = Object.keys(p || {}).filter(function (k) { return p[k] != null && p[k] !== ''; })
      .map(function (k) { return encodeURIComponent(k) + '=' + encodeURIComponent(p[k]); }).join('&');
    return M.urls.find + (q ? '?' + q : '');
  }
  function modelOf(car) { return car.model || car.title || ''; }
  function thumbSrc(p) { return String(p.s || p.m || '').replace(/([?&]width=)\d+/, '$1160'); }
  function copyText(text, okMsg) {
    function fallback() {
      try {
        var ta = document.createElement('textarea'); ta.value = text; ta.setAttribute('readonly', '');
        ta.style.position = 'absolute'; ta.style.left = '-9999px'; document.body.appendChild(ta); ta.select();
        var ok = document.execCommand && document.execCommand('copy'); ta.remove();
        if (ok) { M.toast(okMsg); return; }
      } catch (e) { /* ignore */ }
      M.toast('Copying is blocked here. The link is: ' + text, 6000);
    }
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(function () { M.toast(okMsg); }, fallback);
        return;
      }
    } catch (e) { /* fall through */ }
    fallback();
  }

  /* ------------------------------------------------------------------ tone
     The page's character is COMPUTED from the car's own data: body, drive, fuel,
     engine and which "shop by real life" needs it matches. Nothing is invented.
     (sections/main-car.liquid mirrors the band choice so the first paint matches.) */
  var NEED_ORDER = {
    capability: ['upcountry', 'luxury', 'exec', 'family', 'lowkm'],
    economy: ['hybrid', 'economy', 'hailing', 'lowkm', 'family'],
    executive: ['exec', 'luxury', 'lowkm'],
    family: ['family', 'lowkm', 'hybrid', 'luxury'],
    work: ['upcountry', 'lowkm'],
    everyday: ['economy', 'hailing', 'hybrid', 'lowkm', 'family', 'exec', 'upcountry', 'luxury']
  };
  function engineFuel(car) { return [car.engine, lc(car.fuel)].filter(Boolean).join(' '); }
  function tone(car) {
    var needs = M.needsOf(car);
    var key;
    if (/tractor|truck/i.test(car.body || '')) key = 'work';
    else if (car.drive === '4WD' && /SUV|pickup/i.test(car.body || '')) key = 'capability';
    else if (/hybrid|electric/i.test(car.fuel || '') || needs.indexOf('economy') > -1 || needs.indexOf('hailing') > -1) key = 'economy';
    else if (needs.indexOf('exec') > -1 || needs.indexOf('luxury') > -1) key = 'executive';
    else if (/minivan/i.test(car.body || '') || needs.indexOf('family') > -1) key = 'family';
    else key = 'everyday';
    var order = NEED_ORDER[key];
    var ordered = order.filter(function (k) { return needs.indexOf(k) > -1; })
      .concat(needs.filter(function (k) { return order.indexOf(k) < 0; }));

    var F = {
      mileage: M.kmKnown(car) ? { label: 'Mileage', value: M.km(car.km), sub: car.km === 0 ? 'Delivery kilometres' : car.km < 50000 ? 'Under 50,000 km' : '' } : { label: 'Mileage', value: 'Not listed', sub: '' },
      fuel: { label: 'Fuel', value: car.fuel || 'Not listed', sub: /hybrid/i.test(car.fuel || '') ? 'Petrol-electric' : '' },
      engine: { label: 'Engine', value: car.engine || 'Not listed', sub: '' },
      engfuel: { label: 'Engine', value: car.engine || 'Not listed', sub: car.fuel || '' },
      drive: { label: 'Drive', value: car.drive || 'Not listed', sub: car.drive === '4WD' ? 'Four-wheel drive' : '' },
      trans: { label: 'Gearbox', value: car.trans || 'Not listed', sub: '' },
      body: { label: 'Body', value: car.body || 'Not listed', sub: '' },
      cond: { label: 'Condition', value: car.cond || 'Not listed', sub: car.cond === 'New' && car.year ? 'Year ' + car.year : '' },
      colour: { label: 'Colour', value: cap(car.colour) || 'Not listed', sub: '' }
    };
    var facts = {
      capability: ['drive', 'engfuel', 'mileage', 'trans'],
      economy: ['fuel', 'engine', 'mileage', 'body'],
      executive: ['engfuel', 'mileage', 'trans', 'drive'],
      family: ['body', 'mileage', 'engfuel', 'trans'],
      work: ['cond', 'drive', 'trans', 'fuel'],
      everyday: ['mileage', 'fuel', 'engine', 'trans']
    }[key].map(function (k) { return F[k]; });

    var eyebrow = {
      capability: [car.drive === '4WD' ? 'Four-wheel drive' : '', engineFuel(car), car.body],
      economy: [car.fuel, car.engine, car.body],
      executive: [car.body, engineFuel(car), car.trans],
      family: [car.body, engineFuel(car), car.trans],
      work: [car.cond, car.drive, car.body],
      everyday: [car.body, engineFuel(car), car.trans]
    }[key].filter(Boolean).map(cap).join(' · ');

    var band = { capability: 'night', executive: 'dusk', economy: 'day', family: 'warm', work: 'stone', everyday: 'day' }[key];
    return { key: key, needs: ordered, lead: ordered[0] ? M.need(ordered[0]) : null, facts: facts, eyebrow: eyebrow, band: band };
  }

  /* ------------------------------------------------------------------ gallery */
  function slidesHTML(car, opts) {
    var n = car.photos.length;
    return car.photos.map(function (p, i) {
      var alt = (p.alt && p.alt !== car.title) ? p.alt : (car.title + ', photo ' + (i + 1) + ' of ' + n);
      return '<figure class="cv-slide" role="group" aria-roledescription="slide" aria-label="' + (i + 1) + ' of ' + n + '" data-i="' + i + '">' +
        M.img(p, { sizes: opts.sizes, eager: opts.eagerFirst && i === 0, priority: 'high', alt: alt }) +
        '</figure>';
    }).join('');
  }
  function thumbsHTML(car, cur, cls) {
    var n = car.photos.length;
    return car.photos.map(function (p, i) {
      var h = (p.w && p.h) ? Math.round(64 * (p.h / p.w)) : 48;
      return '<button type="button" class="' + cls + '" data-cv-go="' + i + '" aria-label="Show photo ' + (i + 1) + ' of ' + n + '"' + (i === cur ? ' aria-current="true"' : '') + '>' +
        '<img src="' + esc(thumbSrc(p)) + '" width="64" height="' + h + '" alt="" loading="lazy" decoding="async"></button>';
    }).join('');
  }
  function galleryHTML(car, s) {
    var n = car.photos.length, one = n <= 1;
    if (!n) {
      return '<div class="cv-gallery is-single is-empty"><div class="cv-stage"><div class="cv-nophoto">' + M.icon('camera', { size: 28 }) +
        '<p>Photos coming</p><a class="text-link" ' + M.waAttrs(M.carMessage(car, 'Can you send me photos of it?')) + '>Ask for photos on WhatsApp <span class="arr">→</span></a></div></div></div>';
    }
    return '<div class="cv-gallery' + (one ? ' is-single' : '') + '">' +
      '<div class="cv-stage">' +
        '<div class="cv-track" tabindex="0" role="region" aria-roledescription="carousel" aria-label="Photos of the ' + esc(car.title) + (one ? '' : '. Use the arrow keys to move between photos.') + '">' +
          slidesHTML(car, { sizes: '(max-width: 900px) 100vw, 820px', eagerFirst: true }) +
        '</div>' +
        (one ? '' :
          '<button type="button" class="icon-btn icon-btn--dark cv-nav cv-nav--prev" data-cv-step="-1" aria-label="Previous photo">' + M.icon('chevron-left') + '</button>' +
          '<button type="button" class="icon-btn icon-btn--dark cv-nav cv-nav--next" data-cv-step="1" aria-label="Next photo">' + M.icon('chevron-right') + '</button>') +
        '<div class="cv-stage-foot">' +
          '<span class="cv-count t-num" aria-live="polite"><span><span data-cv-cur>' + (s.idx + 1) + '</span> / ' + n + '</span></span>' +
          (one ? '<a class="cv-soon" ' + M.waAttrs(M.carMessage(car, 'Can you send me more photos?')) + '>' + M.icon('camera', { size: 16 }) + 'Ask for more photos</a>' : '') +
          '<button type="button" class="cv-full" data-cv-full aria-label="View photos fullscreen">' + M.icon('expand', { size: 18 }) + '<span aria-hidden="true">Fullscreen</span></button>' +
        '</div>' +
      '</div>' +
      (one ? '' : '<div class="cv-thumbs" aria-label="Choose a photo">' + thumbsHTML(car, s.idx, 'cv-thumb') + '</div>') +
    '</div>';
  }
  function trackGo(track, i, instant) {
    if (!track) return;
    var w = track.clientWidth; if (!w) return;
    track.scrollTo({ left: i * w, behavior: instant ? 'auto' : smooth() });
  }
  function trackIndex(track) { var w = track.clientWidth || 1; return Math.round(track.scrollLeft / w); }
  function bindTrack(track, n, onChange) {
    var raf = 0;
    track.addEventListener('scroll', function () {
      if (raf) return;
      raf = requestAnimationFrame(function () { raf = 0; onChange(Math.max(0, Math.min(n - 1, trackIndex(track)))); });
    }, { passive: true });
    track.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault(); e.stopPropagation();
        var i = trackIndex(track) + (e.key === 'ArrowRight' ? 1 : -1);
        trackGo(track, Math.max(0, Math.min(n - 1, i)));
      }
    });
  }
  function setIdx(root, car, i) {
    var s = st(car.ref); s.idx = i;
    var cur = root.querySelector('[data-cv-cur]'); if (cur) cur.textContent = i + 1;
    root.querySelectorAll('.cv-thumb').forEach(function (t, k) { if (k === i) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current'); });
    var prev = root.querySelector('.cv-nav--prev'), next = root.querySelector('.cv-nav--next');
    if (prev) prev.disabled = i <= 0;
    if (next) next.disabled = i >= car.photos.length - 1;
  }

  /* Fullscreen viewer: M.modal (role=dialog, aria-modal, focus trap, Esc) in #layer-root.
     Arrow keys + swipe (scroll-snap) + thumbnails. Reduced motion: instant jumps. */
  function openFullscreen(root, car) {
    var s = st(car.ref), n = car.photos.length;
    if (!n) return null;
    var node = document.createElement('div');
    node.className = 'cv-fs';
    node.innerHTML =
      '<div class="cv-fs-head"><p class="cv-fs-title">' + esc(car.title) + ' <span class="cv-fs-ref">Ref ' + esc(car.ref) + '</span></p>' +
        '<span class="cv-fs-count t-num" aria-live="polite"><span data-fs-cur>' + (s.idx + 1) + '</span> / ' + n + '</span></div>' +
      '<div class="cv-fs-stage">' +
        '<div class="cv-fs-track" tabindex="0" role="region" aria-roledescription="carousel" aria-label="Photos, fullscreen">' +
          slidesHTML(car, { sizes: '100vw', eagerFirst: false }) +
        '</div>' +
        (n > 1 ? '<button type="button" class="icon-btn icon-btn--dark cv-fs-nav cv-fs-nav--prev" data-fs-step="-1" aria-label="Previous photo">' + M.icon('chevron-left') + '</button>' +
                 '<button type="button" class="icon-btn icon-btn--dark cv-fs-nav cv-fs-nav--next" data-fs-step="1" aria-label="Next photo">' + M.icon('chevron-right') + '</button>' : '') +
      '</div>' +
      (n > 1 ? '<div class="cv-fs-thumbs" aria-label="Choose a photo">' + thumbsHTML(car, s.idx, 'cv-fs-thumb') + '</div>' : '');
    var imgs = node.querySelectorAll('.cv-fs-track img');
    var track = node.querySelector('.cv-fs-track');
    var fsIdx = s.idx;
    function sync(i) {
      fsIdx = i;
      node.querySelector('[data-fs-cur]').textContent = i + 1;
      node.querySelectorAll('.cv-fs-thumb').forEach(function (t, k) { if (k === i) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current'); });
      var p = node.querySelector('.cv-fs-nav--prev'), q = node.querySelector('.cv-fs-nav--next');
      if (p) p.disabled = i <= 0; if (q) q.disabled = i >= n - 1;
    }
    var m = M.modal({
      label: 'Photos of the ' + car.title, content: node, full: true, cls: 'cv-fs-panel', focus: '.cv-fs-track',
      onKey: function (e) {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          var t = e.target; if (t && /INPUT|TEXTAREA|SELECT/.test(t.tagName)) return;
          e.preventDefault();
          var i = Math.max(0, Math.min(n - 1, fsIdx + (e.key === 'ArrowRight' ? 1 : -1)));
          trackGo(track, i);
        }
      },
      onClose: function () {
        var gTrack = root.querySelector('.cv-track');
        if (gTrack) { trackGo(gTrack, fsIdx, true); setIdx(root, car, fsIdx); }
      }
    });
    // the photo the viewer opens on, and its neighbours, load now; the rest stay lazy
    [s.idx - 1, s.idx, s.idx + 1].forEach(function (k) { if (imgs[k]) imgs[k].setAttribute('loading', 'eager'); });
    bindTrack(track, n, sync);
    requestAnimationFrame(function () { trackGo(track, s.idx, true); sync(s.idx); });
    node.addEventListener('click', function (e) {
      var st1 = e.target.closest('[data-fs-step]'); if (st1) { trackGo(track, Math.max(0, Math.min(n - 1, fsIdx + Number(st1.getAttribute('data-fs-step'))))); return; }
      var g = e.target.closest('[data-cv-go]'); if (g) { trackGo(track, Number(g.getAttribute('data-cv-go'))); }
    });
    return m;
  }

  /* ------------------------------------------------------------------ enquiry
     WhatsApp message = greeting (+ name) + car title + Ref + chosen questions + note + product URL. */
  function buildMessage(car, s) {
    var who = s.name.trim() ? ', this is ' + s.name.trim() : '';
    var qs = s.qs.length ? ': ' + s.qs.join(' ') : '.';
    var note = s.note.trim() ? ' ' + s.note.trim().replace(/\s+/g, ' ') : '';
    return 'Hi AHIHQ Motors' + who + ', I am asking about the ' + car.title + ' (Ref ' + car.ref + ')' + qs + note + ' ' + M.carUrl(car);
  }
  function enquiryHTML(el, car, s) {
    var missing = !M.whatsapp;
    return '<aside class="cv-enq" id="cv-enquire" aria-labelledby="cv-enq-h">' +
      '<p class="eyebrow">Enquire</p>' +
      '<h2 class="cv-enq-h t-display-s" id="cv-enq-h">' + esc(cfg(el, 'enq-h', 'Ask about this car')) + '</h2>' +
      '<p class="cv-enq-sub t-small">' + esc(cfg(el, 'enq-sub', 'Pick your questions. WhatsApp opens with the message ready, including the car, its Ref and this page. Nothing is sent until you press send there.')) + '</p>' +
      '<fieldset class="cv-chips"><legend class="sr-only">Questions to ask</legend>' +
        M.QUESTIONS.map(function (q) { return '<button type="button" class="chip cv-chip" data-cv-q="' + esc(q) + '" aria-pressed="' + (s.qs.indexOf(q) > -1) + '">' + esc(q) + '</button>'; }).join('') +
      '</fieldset>' +
      '<div class="cv-enq-fields">' +
        '<div class="field"><label for="cv-name">Your name <span class="cv-opt">optional</span></label><input class="input" id="cv-name" name="name" autocomplete="name" value="' + esc(s.name) + '"></div>' +
        '<div class="field"><label for="cv-note">Anything else <span class="cv-opt">optional</span></label><textarea class="textarea cv-note" id="cv-note" name="note" rows="2" placeholder="e.g. I can view it on Saturday morning">' + esc(s.note) + '</textarea></div>' +
      '</div>' +
      '<div class="cv-msg"><p class="cv-msg-label t-label-s" id="cv-msg-l">Your message</p><p class="cv-msg-text" aria-labelledby="cv-msg-l" data-cv-msg>' + esc(buildMessage(car, s)) + '</p></div>' +
      (missing ? needed('No WhatsApp number is set, so this button opens the contact page. Add the number in Theme settings.') : '') +
      '<div class="cv-enq-actions">' +
        '<a class="btn btn--primary btn--block cv-send" data-cv-send ' + M.waAttrs(buildMessage(car, s)) + '>' + M.icon('whatsapp') + 'Send on WhatsApp</a>' +
        '<button type="button" class="btn btn--secondary btn--block" data-cv-copy>' + M.icon('doc') + 'Copy message</button>' +
      '</div>' +
    '</aside>';
  }
  function syncEnquiry(root, car) {
    var s = st(car.ref), msg = buildMessage(car, s);
    var t = root.querySelector('[data-cv-msg]'); if (t) t.textContent = msg;
    var a = root.querySelector('[data-cv-send]'); if (a) M.waApply(a, msg);
    root.querySelectorAll('[data-cv-q]').forEach(function (b) { b.setAttribute('aria-pressed', String(s.qs.indexOf(b.getAttribute('data-cv-q')) > -1)); });
  }

  /* ------------------------------------------------------------------ sections */
  function specRows(car) {
    var a = M.avail(car);
    var rows = [
      ['Make', car.make], ['Model', car.model], ['Grade / trim', car.grade], ['Year', car.year ? String(car.year) : ''],
      ['Mileage', M.kmKnown(car) ? M.km(car.km) : ''], ['Fuel', car.fuel], ['Engine', car.engine], ['Gearbox', car.trans],
      ['Drive', car.drive], ['Body', car.body], ['Colour', cap(car.colour)], ['Condition', car.cond],
      ['Availability', a.text]
    ];
    if (car.status === 'way' && car.etaFuture) rows.push(['Arrival', 'Due ' + M.date(car.eta, true)]);
    rows.push(['Stock reference', car.ref]);
    return '<table class="cv-spec"><caption class="sr-only">Specifications of the ' + esc(car.title) + '</caption><tbody>' +
      rows.filter(function (r) { return r[1] != null && String(r[1]).trim() !== ''; }).map(function (r) {
        return '<tr><th scope="row">' + esc(r[0]) + '</th><td>' + esc(r[1]) + '</td></tr>';
      }).join('') + '</tbody></table>';
  }
  function factLine(car) {
    // A factual line assembled from the record (never invented).
    var a = M.avail(car);
    var head = [lc(car.colour), car.year, car.make, car.model].filter(Boolean).join(' ');
    var what = [car.fuel ? lc(car.fuel) : '', /^[A-Z0-9]+$/.test(car.body || '') ? car.body : lc(car.body)].filter(Boolean).join(' ');
    var parts = [
      (what ? 'a ' + what : '') + (M.kmKnown(car) ? (what ? ' with ' : 'with ') + M.km(car.km) : ''),
      lc(car.trans), car.drive, car.engine ? car.engine + ' engine' : ''
    ].filter(function (x) { return x && String(x).trim(); });
    var where = car.status === 'in' ? 'Here now' + (car.loc ? ' in ' + car.loc : '') + '.' : a.text + '.';
    return (head ? 'A ' + head : car.title) + (parts.length ? ', ' + parts.join(', ') : '') + '. ' + where;
  }
  function storyHTML(el, car, t) {
    var desc = descHTML || (car.desc ? '<p>' + esc(car.desc) + '</p>' : '');
    return '<section class="cv-sec cv-story" aria-labelledby="cv-story-h">' +
      '<p class="eyebrow">The car</p><h2 class="t-display-m" id="cv-story-h">About this ' + esc(modelOf(car)) + '</h2>' +
      '<p class="cv-fact t-body-l measure">' + esc(factLine(car)) + '</p>' +
      (desc ? '<div class="cv-about rte measure">' + desc + '</div>'
            : needed('A short, honest description of this particular car in the product description: history, service record, anything a buyer should know.', 'cv-needed')) +
      (t.needs.length ? '<div class="cv-suits"><p class="t-label-s">Suited to</p><ul class="cv-suits-list" role="list">' +
        t.needs.map(function (k) { var n = M.need(k); return n ? '<li><a class="chip cv-suit" href="' + esc(M.stockUrl({ need: k })) + '">' + esc(n.label) + '</a></li>' : ''; }).join('') +
        '</ul><p class="t-xs cv-suits-note">Matched from this car’s make, model, fuel and mileage. Each link shows similar cars.</p></div>' : '') +
    '</section>';
  }
  function featuresHTML(car) {
    var f = uniqCI(car.features);
    if (!f.length) return '';
    return '<section class="cv-sec" aria-labelledby="cv-feat-h"><p class="eyebrow">Equipment</p><h2 class="t-display-s" id="cv-feat-h">Features <span class="cv-h-count t-num">' + f.length + '</span></h2>' +
      '<ul class="cv-feat" role="list">' + f.map(function (x) { return '<li>' + M.icon('check', { size: 16 }) + '<span>' + esc(x) + '</span></li>'; }).join('') + '</ul>' +
      '<p class="t-xs cv-src">As listed by the showroom. Confirm anything that matters to you before a viewing.</p></section>';
  }
  function conditionHTML(el, car) {
    return '<section class="cv-sec" aria-labelledby="cv-cond-h"><p class="eyebrow">Condition</p><h2 class="t-display-s" id="cv-cond-h">Condition &amp; inspection</h2>' +
      '<dl class="cv-kv"><div><dt>Condition</dt><dd>' + esc(car.cond || 'Not listed') + '</dd></div><div><dt>Mileage</dt><dd class="t-num">' + esc(M.kmKnown(car) ? M.km(car.km) : 'Not listed') + '</dd></div><div><dt>Inspection</dt><dd>' + esc(cfg(el, 'insp-short', '21-point, by professionals')) + '</dd></div></dl>' +
      '<p class="measure">' + esc(cfg(el, 'insp-text', 'Every car we sell goes through a 21-point inspection by professionals before you commit. Ask us about this car and we will walk you through it.')) + '</p>' +
      '<p class="t-small cv-ask"><a class="text-link" ' + M.waAttrs(M.carMessage(car, 'Can you share its inspection and service history?')) + '>Ask for its history on WhatsApp <span class="arr">→</span></a></p></section>';
  }
  var PIPE = [['japan', 'In Japan'], ['transit', 'In transit'], ['port', 'At Mombasa port'], ['here', 'Here now']];
  function stageKey(car) { if (car.status === 'in') return 'here'; var s = car.stage || ''; return /Japan/i.test(s) ? 'japan' : /transit/i.test(s) ? 'transit' : /port/i.test(s) ? 'port' : 'japan'; }
  function availabilityHTML(el, car) {
    var a = M.avail(car), partner = isPartner(), city = car.loc || '';
    var body;
    if (car.status === 'in') {
      body = '<p class="cv-avail-big">' + M.availHTML(car) + '</p>' +
        '<p class="measure">' + (city ? 'This car is in ' + esc(city) + '. ' : '') + 'Before you travel we confirm it is still there, then arrange a time to view it.</p>';
    } else {
      var k = stageKey(car), ki = PIPE.map(function (p) { return p[0]; }).indexOf(k);
      body = '<p class="cv-avail-big">' + M.availHTML(car) + '</p>' +
        '<ol class="cv-pipe" role="list">' + PIPE.map(function (p, i) {
          var state = i < ki ? 'done' : i === ki ? 'now' : 'next';
          return '<li class="is-' + state + '"' + (i === ki ? ' aria-current="step"' : '') + '><span class="cv-pipe-dot" aria-hidden="true"></span><span>' + esc(p[1]) + (i === ki && a.due ? ' <b class="t-num">· ' + esc(a.due) + '</b>' : '') + '</span><span class="sr-only">' + (state === 'done' ? ' (done)' : state === 'now' ? ' (current stage)' : '') + '</span></li>';
        }).join('') + '</ol>' +
        (!car.etaFuture ? '<p class="t-small cv-muted">Ask us for the latest arrival date.</p>' : '');
    }
    var ph = phone();
    var contact = ph
      ? 'Call or WhatsApp <a href="' + esc(telHref(ph)) + '">' + esc(ph) + '</a> to arrange a viewing.'
      : '<a ' + M.waAttrs(M.carMessage(car, 'Can I book a viewing?')) + '>Message us on WhatsApp</a> to arrange a viewing.';
    var showroom = car.status === 'in' || city
      ? '<div class="cv-room"><h3 class="t-label">' + M.icon('pin', { size: 16 }) + esc(city ? city + (partner ? ' partner showroom' : ' showroom') : 'Showroom') + '</h3>' +
          '<p class="t-small">' + contact + ' Collect the car from the showroom once the deal is done.</p>' +
          '<p class="t-small cv-muted">Viewings are by appointment; we arrange the time with you.</p></div>'
      : '';
    var partnerNote = partner
      ? '<div class="cv-partner"><h3 class="t-label">How our stock network works</h3><p class="t-small">Cars like this one are held in partner showrooms in Nairobi and Mombasa, not all owned by AHIHQ Motors. We are your point of contact: we check the car is still there, arrange the viewing and help with the paperwork.</p></div>'
      : '';
    return '<section class="cv-sec" id="cv-availability" aria-labelledby="cv-av-h"><p class="eyebrow">Availability</p><h2 class="t-display-s" id="cv-av-h">Where it is now</h2>' +
      body + (showroom || partnerNote ? '<div class="cv-rooms">' + showroom + partnerNote + '</div>' : '') + '</section>';
  }
  function moneyHTML(el, car) {
    function card(title, body, q) {
      return '<div class="cv-q"><h3 class="t-display-s cv-q-h">' + esc(title) + '</h3><p class="t-small">' + esc(body) + '</p>' +
        '<a class="text-link" ' + M.waAttrs(M.carMessage(car, q)) + '>Ask: “' + esc(q) + '” <span class="arr">→</span></a></div>';
    }
    return '<section class="cv-sec" aria-labelledby="cv-money-h"><p class="eyebrow">Paying for it</p><h2 class="t-display-s" id="cv-money-h">Finance and trade-in</h2>' +
      '<p class="t-small cv-muted measure">Price on request: ask and we come back with today’s price.</p>' +
      '<div class="cv-qs">' +
        card(cfg(el, 'fin-h', 'Finance available'), cfg(el, 'fin-text', 'Hire purchase and bank finance, arranged through our finance partners. Tell us how you want to pay and we set it up.'), 'Do you offer finance?') +
        card(cfg(el, 'trade-h', 'Trade-in accepted'), cfg(el, 'trade-text', 'Bring your current car to the viewing. We value it there and take it off the price of this one.'), 'Can I trade in my car?') +
      '</div></section>';
  }
  function similar(car, pool, max) {
    var needs = M.needsOf(car), nonRoad = M.NON_ROAD.test(car.body || '');
    return pool.filter(function (c) { return c.ref !== car.ref && M.NON_ROAD.test(c.body || '') === nonRoad; }).map(function (c) {
      var sc = 0;
      if (c.make === car.make) sc += 100; if (c.make === car.make && c.model === car.model) sc += 50;
      if (c.body && c.body === car.body) sc += 40;
      M.needsOf(c).forEach(function (k) { if (needs.indexOf(k) > -1) sc += 6; });
      if (c.status === 'in') sc += 3;
      if (c.year && car.year) sc -= Math.abs(c.year - car.year) * 0.5;
      return { c: c, sc: sc };
    }).sort(function (a, b) { return b.sc - a.sc; }).slice(0, max).map(function (x) { return x.c; });
  }
  function relatedWhy(car, c) {
    if (c.make === car.make) return 'Same make';
    if (c.body && c.body === car.body) return 'Same body: ' + c.body;
    return 'Similar use';
  }
  function relList(list, car, cars) {
    cars.forEach(function (c) {
      var li = document.createElement('li'); li.className = 'cv-rel-item';
      li.innerHTML = '<p class="cv-rel-why t-label-s">' + esc(relatedWhy(car, c)) + '</p>';
      li.appendChild(M.card(c, { variant: 'compact', headingLevel: 'h3', sizes: '(max-width: 600px) 80vw, 300px' }));
      list.appendChild(li);
    });
  }
  function relatedFill(el, car, ctx) {
    var wrap = el.querySelector('[data-cv-relwrap]'); if (!wrap) return;
    if (el.getAttribute('data-related') === 'false') { wrap.innerHTML = ''; return; }
    var max = Math.max(1, Math.min(12, parseInt(cfg(el, 'related-count', '4'), 10) || 4));
    var rel = similar(car, ctx.cars, max);
    if (rel.length) {
      wrap.innerHTML = '<section class="cv-rel env-paper" aria-labelledby="cv-rel-h"><div class="wrap">' +
        '<div class="cv-rel-head"><div><p class="eyebrow">More like this</p><h2 class="t-display-m" id="cv-rel-h">' + esc(cfg(el, 'rel-h', 'Related cars')) + '</h2></div>' +
        '<a class="text-link" href="' + esc(M.stockUrl({ make: lc(car.make) })) + '">All ' + esc(car.make) + ' stock <span class="arr">→</span></a></div>' +
        '<ul class="cv-rel-list" role="list" data-cv-rel></ul></div></section>';
      relList(wrap.querySelector('[data-cv-rel]'), car, rel);
    } else if (renders > 1 && M.NON_ROAD.test(car.body || '')) {
      // only once the feed is in: before that "the only one" would be a guess
      wrap.innerHTML = '<section class="cv-rel env-paper" aria-labelledby="cv-rel-h"><div class="wrap cv-rel-none">' +
        '<p class="eyebrow">More like this</p><h2 class="t-display-m" id="cv-rel-h">Looking for farm or commercial machinery?</h2>' +
        '<p class="t-body measure">This is the only ' + esc(lc(car.body)) + ' on the site today. Tell the concierge what the work is and we look through the network for machines, not cars.</p>' +
        '<div class="cv-cg-cta"><a class="btn btn--secondary" href="' + esc(findUrl({ ref: car.ref })) + '">Ask the concierge</a></div></div></section>';
    } else {
      wrap.innerHTML = '';
    }
    M.syncSaved();
  }
  function conciergeHTML(el, car) {
    if (el.getAttribute('data-concierge') === 'false') return '';
    var steps = [
      ['Tell us what you need', 'This car, or what you liked about it.'],
      ['We search the network', 'Showrooms in Nairobi and Mombasa, and cars on the way.'],
      ['We confirm availability and history', 'Before you spend a morning travelling.'],
      ['We arrange the viewing', 'A time that suits you.'],
      ['We negotiate', 'On your behalf.'],
      ['We handle the paperwork', 'Transfer and documents.'],
      ['You collect the car', 'From the Nairobi or Mombasa showroom.']
    ];
    return '<section class="cv-concierge env-stone" aria-labelledby="cv-cg-h"><div class="wrap cv-cg-inner">' +
      '<div class="cv-cg-intro"><p class="eyebrow">The concierge</p><h2 class="t-display-m" id="cv-cg-h">' + esc(cfg(el, 'cg-h', 'Not quite the one?')) + '</h2>' +
      '<p class="cv-cg-voice t-serif">' + esc(cfg(el, 'cg-text', 'Your next car, found and handled for you. Tell us what matters and we look across the whole network, not just the cars on this site.')) + '</p>' +
      '<div class="cv-cg-cta"><a class="btn btn--primary" href="' + esc(findUrl({ ref: car.ref, make: car.make, model: car.model })) + '">Find me a car</a>' +
      '<a class="btn btn--secondary" ' + M.waAttrs('Hi AHIHQ Motors, I saw the ' + car.title + ' (Ref ' + car.ref + ') and would like help finding something similar. ' + M.carUrl(car)) + '>' + M.icon('whatsapp') + 'Ask on WhatsApp</a></div></div>' +
      '<ol class="cv-steps" role="list">' + steps.map(function (s, i) {
        return '<li><span class="cv-step-n t-num" aria-hidden="true">' + (i < 9 ? '0' : '') + (i + 1) + '</span><div><p class="cv-step-t">' + esc(s[0]) + '</p>' + (s[1] ? '<p class="t-small cv-muted">' + esc(s[1]) + '</p>' : '') + '</div></li>';
      }).join('') + '</ol>' +
    '</div></section>';
  }
  function barHTML(car) {
    var ph = phone();
    var left = ph
      ? '<a class="btn btn--secondary cv-bar-call" href="' + esc(telHref(ph)) + '">' + M.icon('phone') + 'Call</a>'
      : '<a class="btn btn--secondary cv-bar-call" ' + M.waAttrs(M.carMessage(car, M.QUESTIONS[0])) + '>' + M.icon('whatsapp') + 'WhatsApp</a>';
    return '<div class="cv-bar" data-cv-bar><div class="cv-bar-in">' + left +
      '<a class="btn btn--primary cv-bar-enq" href="#cv-enquire" data-cv-enquire>Enquire</a></div></div>';
  }

  /* ------------------------------------------------------------------ car view */
  function renderCar(el, ctx) {
    var ref = ctx.route && ctx.route.view === 'car' ? ctx.route.ref : null;
    var car = ref ? M.byRef(ref) : null;
    if (!car) return; // keep the server-rendered page as it is
    renders++;
    // Feed arrived after the first paint: only the related cars change. Re-rendering the
    // whole page would drop focus/typing in the enquiry form and reset the gallery.
    if (curRef === car.ref && el.querySelector('.cv[data-ref]')) { relatedFill(el, car, ctx); return; }
    if (io) { io.disconnect(); io = null; }
    var s = st(car.ref);
    if (s.idx >= car.photos.length) s.idx = 0;
    var t = tone(car);
    var dark = t.band === 'night' || t.band === 'dusk';
    var logo = M.marqueSVG(car.make, { size: 28, cls: 'cv-logo' });

    el.innerHTML =
      '<article class="cv cv--' + t.key + '" data-ref="' + esc(car.ref) + '" aria-labelledby="cv-h1">' +
      /* ---- first viewport ---- */
      '<section class="cv-top cv-band--' + t.band + (dark ? ' on-dark tex' : '') + '">' +
        '<div class="wrap">' +
          '<nav class="cv-crumbs" aria-label="Breadcrumb"><ol role="list">' +
            '<li><a href="' + esc(M.urls.stock) + '">Stock</a></li>' +
            (car.make ? '<li><a href="' + esc(M.stockUrl({ make: lc(car.make) })) + '">' + esc(car.make) + '</a></li>' : '') +
            '<li><span aria-current="page">' + esc([modelOf(car), car.year].filter(Boolean).join(' ')) + '</span></li>' +
          '</ol><span class="cv-crumb-ref t-num">Ref ' + esc(car.ref) + '</span></nav>' +
          '<div class="cv-hero">' +
            galleryHTML(car, s) +
            '<div class="cv-sum">' +
              '<p class="cv-sum-avail">' + M.availHTML(car) + '</p>' +
              '<h1 class="cv-h1" id="cv-h1" tabindex="-1">' +
                '<span class="cv-h1-make">' + (logo || '') + '<span>' + esc([car.year, car.make].filter(Boolean).join(' · ')) + '</span></span> ' +
                '<span class="cv-h1-model">' + esc(modelOf(car)) + '</span>' +
              '</h1>' +
              (t.eyebrow ? '<p class="cv-eyebrow t-label">' + esc(t.eyebrow) + '</p>' : '') +
              '<div class="cv-price"><span class="cv-price-v">Price on request</span><span class="cv-price-n t-small">Ask us for today’s price.</span></div>' +
              '<dl class="cv-facts">' + t.facts.map(function (f) {
                return '<div class="cv-fact-i"><dt>' + esc(f.label) + '</dt><dd class="t-num">' + esc(f.value) + '</dd>' + (f.sub ? '<dd class="cv-fact-sub">' + esc(f.sub) + '</dd>' : '') + '</div>';
              }).join('') + '</dl>' +
              (t.lead ? '<p class="cv-lead"><span class="cv-lead-k">' + esc(t.lead.label) + '</span> ' + esc(t.lead.sub) + '</p>' : '') +
              '<div class="cv-cta">' +
                '<a class="btn btn--primary cv-cta-main" href="#cv-enquire" data-cv-enquire>Enquire about this car</a>' +
                '<div class="cv-cta-row">' +
                  '<a class="btn ' + (dark ? 'btn--ghost-dark' : 'btn--secondary') + ' cv-cta-wa" ' + M.waAttrs(M.carMessage(car, M.QUESTIONS[0])) + ' aria-label="Ask on WhatsApp: is the ' + esc(car.title) + ' still available?">' + M.icon('whatsapp') + '<span>Is it available?</span></a>' +
                  '<button type="button" class="icon-btn' + (dark ? ' icon-btn--dark' : '') + '" data-save="' + esc(car.ref) + '" aria-pressed="' + M.isSaved(car.ref) + '" aria-label="Save the ' + esc(car.title) + '"><span data-heart>' + M.icon(M.isSaved(car.ref) ? 'heart-fill' : 'heart') + '</span></button>' +
                  '<button type="button" class="icon-btn' + (dark ? ' icon-btn--dark' : '') + '" data-cv-share aria-label="Copy a link to this car">' + M.icon('share') + '</button>' +
                '</div>' +
              '</div>' +
            '</div>' +
          '</div>' +
        '</div>' +
      '</section>' +
      /* ---- below the fold ---- */
      '<div class="cv-body env-ivory"><div class="wrap cv-grid">' +
        storyHTML(el, car, t) +
        enquiryHTML(el, car, s) +
        '<section class="cv-sec" aria-labelledby="cv-spec-h"><p class="eyebrow">Specification</p><h2 class="t-display-s" id="cv-spec-h">Full specification</h2>' + specRows(car) + '</section>' +
        featuresHTML(car) +
        conditionHTML(el, car) +
        availabilityHTML(el, car) +
        moneyHTML(el, car) +
      '</div></div>' +
      '<div data-cv-relwrap></div>' +
      conciergeHTML(el, car) +
      /* ---- phone sticky enquiry bar (container ≤900 only) ---- */
      barHTML(car) +
      '</article>';

    relatedFill(el, car, ctx);

    // gallery wiring
    var track = el.querySelector('.cv-track');
    if (track) {
      bindTrack(track, car.photos.length, function (i) { setIdx(el, car, i); });
      var keep = s.idx;
      requestAnimationFrame(function () { trackGo(track, keep, true); setIdx(el, car, keep); });
    }
    // hide the phone bar while the enquiry panel itself is on screen (no duplicate CTA)
    var bar = el.querySelector('[data-cv-bar]'), enq = el.querySelector('#cv-enquire');
    if (bar && enq && 'IntersectionObserver' in window) {
      io = new IntersectionObserver(function (es) { es.forEach(function (e) { bar.classList.toggle('is-tucked', e.isIntersecting); }); }, { threshold: 0.25 });
      io.observe(enq);
    }
    curRef = car.ref;
  }

  function initCar(el) {
    var d = el.querySelector('[data-cv-desc]');
    if (d) descHTML = d.innerHTML.trim();
    el.addEventListener('click', function (e) {
      var car = curRef ? M.byRef(curRef) : null;
      var t;
      if ((t = e.target.closest('[data-cv-step]')) && car) {
        var tr = el.querySelector('.cv-track'); var i = st(car.ref).idx + Number(t.getAttribute('data-cv-step'));
        trackGo(tr, Math.max(0, Math.min(car.photos.length - 1, i))); return;
      }
      if ((t = e.target.closest('.cv-thumb')) && car) { trackGo(el.querySelector('.cv-track'), Number(t.getAttribute('data-cv-go'))); return; }
      if ((t = e.target.closest('[data-cv-full]')) && car) { openFullscreen(el, car); return; }
      if ((t = e.target.closest('.cv-slide')) && car) { st(car.ref).idx = Number(t.getAttribute('data-i')); openFullscreen(el, car); return; }
      if ((t = e.target.closest('[data-cv-enquire]'))) {
        var enq = el.querySelector('#cv-enquire'); if (!enq) return;
        e.preventDefault();
        enq.scrollIntoView({ behavior: smooth(), block: 'start' });
        var first = enq.querySelector('.cv-chip'); if (first) setTimeout(function () { try { first.focus({ preventScroll: true }); } catch (x) { } }, M.reducedMotion ? 0 : 450);
        return;
      }
      if ((t = e.target.closest('[data-cv-q]')) && car) {
        var s = st(car.ref), q = t.getAttribute('data-cv-q'), k = s.qs.indexOf(q);
        if (k > -1) s.qs.splice(k, 1); else s.qs.push(q);
        s.qs.sort(function (a, b) { return M.QUESTIONS.indexOf(a) - M.QUESTIONS.indexOf(b); });
        syncEnquiry(el, car); return;
      }
      if ((t = e.target.closest('[data-cv-copy]')) && car) { copyText(buildMessage(car, st(car.ref)), 'Message copied. Paste it into WhatsApp, SMS or email.'); return; }
      if ((t = e.target.closest('[data-cv-share]')) && car) { copyText(M.carUrl(car), 'Link to the ' + car.title + ' copied.'); return; }
    });
    el.addEventListener('input', function (e) {
      var car = curRef ? M.byRef(curRef) : null; if (!car) return;
      if (e.target.id === 'cv-name') { st(car.ref).name = e.target.value; syncEnquiry(el, car); }
      if (e.target.id === 'cv-note') { st(car.ref).note = e.target.value; syncEnquiry(el, car); }
    });
  }

  M.view('car', { init: initCar, render: renderCar });

  /* ================================================================== SOLD
     A /products/<handle> that 404s is a car that has left the site. We only have its
     handle (e.g. 2021-toyota-prado-32296): match its words against the live feed to
     show similar in-stock cars and prefill "Find me one like it". */
  function slug(s) { return String(s == null ? '' : s).toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''); }
  function handleFromPath(path) {
    var m = String(path || '').match(/\/products\/([^\/?#]+)/);
    if (!m) return '';
    try { return decodeURIComponent(m[1]).toLowerCase(); } catch (e) { return m[1].toLowerCase(); }
  }
  function parseHandle(h) {
    var words = h.split('-').filter(Boolean), year = '', ref = '';
    if (words.length > 1 && /^\d{4,}$/.test(words[words.length - 1]) && !/^(19|20)\d\d$/.test(words[words.length - 1])) ref = words.pop();
    for (var i = 0; i < words.length; i++) { if (/^(19|20)\d\d$/.test(words[i])) { year = words[i]; words.splice(i, 1); break; } }
    return { words: words, year: year, ref: ref, h: '-' + words.join('-') + '-' };
  }
  function soldMatch(info, pool, max) {
    return pool.map(function (c) {
      var sc = 0, mk = slug(c.make), md = slug(c.model);
      var makeHit = mk && info.h.indexOf('-' + mk + '-') > -1;
      if (makeHit) sc += 100;
      if (md && info.h.indexOf('-' + md + '-') > -1) sc += makeHit ? 60 : 30;
      var tw = slug(c.title).split('-');
      info.words.forEach(function (w) { if (w.length > 1 && tw.indexOf(w) > -1 && w !== mk) sc += 8; });
      if (!sc) return null;
      if (info.year && c.year) sc -= Math.min(20, Math.abs(Number(info.year) - c.year) * 1.5);
      return { c: c, sc: sc, makeHit: makeHit };
    }).filter(Boolean).sort(function (a, b) { return b.sc - a.sc; }).slice(0, max);
  }
  function siteCount(make) {
    var mk = String(make || '').toLowerCase(), n = 0;
    (M.all || []).forEach(function (c) { if (String(c.make || '').toLowerCase() === mk) n++; });
    return n;
  }
  function renderSold(el, ctx) {
    var h = handleFromPath(location.pathname);
    if (!h) return; // not a car URL: the section shows the plain not-found
    var list = el.querySelector('[data-sd-sim]'); if (!list) return;
    var info = parseHandle(h);
    el.hidden = false;
    var nf = document.querySelector('[data-nf-root]'); if (nf) nf.hidden = true;
    // what the link itself tells us: year, words, Ref (Liquid only sees "/404")
    var baseTitle = [info.year].concat(info.words.map(cap)).filter(Boolean).join(' ');
    var rEl = el.querySelector('[data-sd-ref]');
    if (rEl) { rEl.textContent = info.ref ? 'Ref ' + info.ref : ''; rEl.hidden = !info.ref; }
    document.title = (baseTitle ? baseTitle + ': no longer available' : 'Car no longer available') + ' · AHIHQ Motors';
    var tEl0 = el.querySelector('[data-sd-title]'); if (tEl0) { tEl0.textContent = baseTitle; tEl0.hidden = !baseTitle; }
    el.querySelectorAll('[data-sd-find]').forEach(function (a) { a.setAttribute('href', findUrl({ q: info.words.join(' '), year: info.year })); });
    var waEl = el.querySelector('[data-sd-wa]');
    if (waEl) M.waApply(waEl, 'Hi AHIHQ Motors, I was looking at the ' + (baseTitle || 'car') + (info.ref ? ' (Ref ' + info.ref + ')' : '') + ' but it is no longer on the site. Can you find me one like it? ' + location.origin + location.pathname);
    var max = Math.max(1, Math.min(12, parseInt(cfg(el, 'similar-count', '4'), 10) || 4));
    var inStock = ctx.cars.filter(function (c) { return c.status === 'in'; });
    var hits = soldMatch(info, inStock, max);
    if (!hits.length) hits = soldMatch(info, ctx.cars, max); // nothing here now: show what is arriving
    var top = hits[0] && hits[0].makeHit ? hits[0].c : null;

    // a proper title and a better prefilled request once we know the make from the feed
    if (top) {
      var mk = slug(top.make), rest = info.h.replace('-' + mk + '-', '-').split('-').filter(Boolean);
      var md = slug(top.model), sameModel = md && info.h.indexOf('-' + md + '-') > -1;
      var title = [info.year, top.make, sameModel ? top.model : rest.map(cap).join(' ')].filter(Boolean).join(' ');
      var tEl = el.querySelector('[data-sd-title]'); if (tEl) tEl.textContent = title;
      if (waEl) M.waApply(waEl, 'Hi AHIHQ Motors, I was looking at the ' + title + (info.ref ? ' (Ref ' + info.ref + ')' : '') + ' but it is no longer on the site. Can you find me one like it? ' + location.origin + location.pathname);
      var params = { make: top.make, model: sameModel ? top.model : rest.join(' '), year: info.year };
      el.querySelectorAll('[data-sd-find]').forEach(function (a) { a.setAttribute('href', findUrl(params)); });
      var net = (M.network && M.network.byMake && M.network.byMake[top.make]) || null;
      var nEl = el.querySelector('[data-sd-net]');
      if (nEl && net && (net.in || net.way)) {
        nEl.innerHTML = M.icon('compass', { size: 16 }) + '<span>Our showroom network lists <b class="t-num">' + M.fmt(net.in || 0) + '</b> ' + esc(top.make) + ' cars in stock and <b class="t-num">' + M.fmt(net.way || 0) + '</b> on the way' +
          (M.network.packDate ? ' (stock list of ' + esc(M.date(M.network.packDate, true)) + ')' : '') + '.' +
          // "not all on this website" only when it is true: the network holds more of this make than the site lists
          (siteCount(top.make) < (net.in || 0) + (net.way || 0) ? ' Not all are on this website.' : ' All of them are on this website.') + '</span>';
        nEl.hidden = false;
      }
    }
    var sec = el.querySelector('[data-sd-simsec]'), none = el.querySelector('[data-sd-none]');
    list.innerHTML = '';
    if (hits.length) {
      var ref = { make: top ? top.make : '', body: '' };
      hits.forEach(function (x) {
        var c = x.c, li = document.createElement('li'); li.className = 'cv-rel-item';
        var why = ref.make && c.make === ref.make ? (slug(c.model) && info.h.indexOf('-' + slug(c.model) + '-') > -1 ? 'Same model' : 'Same make') : 'Similar';
        li.innerHTML = '<p class="cv-rel-why t-label-s">' + esc(why) + '</p>';
        li.appendChild(M.card(c, { variant: 'compact', headingLevel: 'h3', sizes: '(max-width: 600px) 80vw, 300px' }));
        list.appendChild(li);
      });
      list.hidden = false; if (none) none.hidden = true;
    } else {
      list.hidden = true; if (none) none.hidden = false;
    }
    if (sec) sec.hidden = false;
    M.syncSaved();
  }
  M.view('sold', { render: renderSold });
})();
